import { useState } from 'react'
import { Calendar, Plus, Minus } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function DateCalculator() {
  const [date1, setDate1] = useState('')
  const [date2, setDate2] = useState('')
  const [operation, setOperation] = useState('difference')
  const [daysToAdd, setDaysToAdd] = useState(0)
  const [result, setResult] = useState(null)

  const calculateDifference = () => {
    if (!date1 || !date2) {
      alert('Please select both dates')
      return
    }

    const d1 = new Date(date1)
    const d2 = new Date(date2)
    const diffTime = Math.abs(d2 - d1)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    const diffWeeks = Math.floor(diffDays / 7)
    const diffMonths = Math.floor(diffDays / 30.44)
    const diffYears = Math.floor(diffDays / 365.25)

    setResult({
      days: diffDays,
      weeks: diffWeeks,
      months: diffMonths,
      years: diffYears,
      type: 'difference'
    })
  }

  const calculateAddSubtract = () => {
    if (!date1) {
      alert('Please select a date')
      return
    }

    const date = new Date(date1)
    date.setDate(date.getDate() + daysToAdd)
    
    setResult({
      newDate: date.toISOString().split('T')[0],
      formattedDate: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      type: 'addsubtract'
    })
  }

  const handleCalculate = () => {
    if (operation === 'difference') {
      calculateDifference()
    } else {
      calculateAddSubtract()
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Operation:</label>
            <select
              value={operation}
              onChange={(e) => {
                setOperation(e.target.value)
                setResult(null)
              }}
              className="input w-full"
            >
              <option value="difference">Calculate Date Difference</option>
              <option value="addsubtract">Add/Subtract Days</option>
            </select>
          </div>

          {operation === 'difference' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Start Date:</label>
                <input
                  type="date"
                  value={date1}
                  onChange={(e) => setDate1(e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-3">End Date:</label>
                <input
                  type="date"
                  value={date2}
                  onChange={(e) => setDate2(e.target.value)}
                  className="input w-full"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-6 mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Date:</label>
                <input
                  type="date"
                  value={date1}
                  onChange={(e) => setDate1(e.target.value)}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Days to Add/Subtract:</label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDaysToAdd(Math.max(-365, daysToAdd - 1))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <input
                    type="number"
                    value={daysToAdd}
                    onChange={(e) => setDaysToAdd(Number(e.target.value))}
                    className="input flex-1 text-center"
                    min="-365"
                    max="365"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setDaysToAdd(Math.min(365, daysToAdd + 1))}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Use negative numbers to subtract days
                </p>
              </div>
            </div>
          )}

          <Button onClick={handleCalculate} variant="primary" className="w-full">
            <Calendar className="w-5 h-5 mr-2" />
            Calculate
          </Button>
        </Card.Body>
      </Card>

      {result && (
        <Card>
          <Card.Body>
            {result.type === 'difference' ? (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Difference:</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{result.days}</div>
                    <div className="text-sm text-gray-600 mt-1">Days</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{result.weeks}</div>
                    <div className="text-sm text-gray-600 mt-1">Weeks</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{result.months}</div>
                    <div className="text-sm text-gray-600 mt-1">Months</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{result.years}</div>
                    <div className="text-sm text-gray-600 mt-1">Years</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Result:</h3>
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-2">{result.formattedDate}</div>
                  <div className="text-sm text-gray-600">{result.newDate}</div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {!result && (
        <Card>
          <Card.Body className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select dates and click "Calculate" to see results</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

